import QuizBanners from "@/components/banners/quiz-banners";
import EmptyState from "@/components/empty-state";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useEditionById } from "@/hooks/edition-by-id.hook";
import { currentEditionIdAtom } from "@/store/edition-id.store";
import { Graph } from "iconsax-react";
import { useSetAtom } from "jotai";
import { FC, useEffect } from "react";
import { Link } from "react-router-dom";
import EditionDashboard from "../edition-dashboard";
import EnrollmentBanner from "../enrollment-banner";
import WaitingApplicationApproval from "../waiting-application-approval";
import MilestonesDropdown from "./milestones/milestones-dropdown";
import RelevantDocumentsDropdown from "./relevant-documents/relevant-documents-dropdown";

type EditionSummaryProps = {
  editionId: string;
};

const EditionSummary: FC<EditionSummaryProps> = (props) => {
  const setCurrentEditionIdAtom = useSetAtom(currentEditionIdAtom);

  const { data: userProfile } = useCurrentUserProfile();

  const { data: edition, isLoading } = useEditionById({
    companyId: userProfile?.companyId || "",
    editionId: props.editionId,
  });

  useEffect(() => {
    // Set the editionId in the atom to be used on the child components
    setCurrentEditionIdAtom(props.editionId);
  }, [props.editionId]);

  if (isLoading) {
    return (
      <Skeleton className="my-8 bg-gray-200" style={{ height: "180px" }} />
    );
  }

  return edition ? (
    <Accordion
      type="single"
      collapsible
      defaultValue={`edition-${props.editionId}`}
    >
      <AccordionItem value={`edition-${props.editionId}`}>
        <div className="flex gap-4">
          <AccordionTrigger className="items-center text-xl font-semibold text-[#9491CF]">
            Edición: {edition.name}
          </AccordionTrigger>

          <div className="flex grow items-center justify-end gap-2">
            <RelevantDocumentsDropdown />
            <MilestonesDropdown />
          </div>
        </div>

        <AccordionContent className="pb-0">
          <EnrollmentBanner className="mb-4" />
          <WaitingApplicationApproval className="mb-4" />

          <QuizBanners className="mb-4" />

          {edition.status === "FINISHED" && (
            <div className="flex items-center gap-4">
              <p className="grow">
                Estás visualizando los datos de la edición en la que
                participaste.
              </p>

              <Button className="shrink-0" asChild>
                <Link to={`/previous-editions/${edition.id}`}>
                  <Graph className="mr-2" />
                  Ver resultados
                </Link>
              </Button>
            </div>
          )}

          <EditionDashboard />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  ) : (
    <Card>
      <CardContent className="py-12">
        <EmptyState
          className="mx-auto"
          message={`No se pudo cargar la edición ${props.editionId}`}
        />
      </CardContent>
    </Card>
  );
};

export default EditionSummary;
